/**
 * DOCUMENTATION:
 * Component: AIIntroRAG
 * Purpose: Teaching the concept of Retrieval-Augmented Generation (RAG).
 * * * Note for Junior Developers:
 * - Embedding: The process of turning a word/sentence into a vector.
 * - Vector: A list of numbers that represents the "meaning" of the text.
 * - Cosine Similarity: The math we use to find how "close" two meanings are.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, Stack, Alert, Paper
} from "@mui/material";

/* ✅ Math Rendering */
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';

/* Icons */
import PsychologyIcon from '@mui/icons-material/Psychology';
import SearchIcon from '@mui/icons-material/Search';
import FunctionsIcon from '@mui/icons-material/Functions';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a0519",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const ContentCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(167, 139, 250, 0.2)",
    color: "white",
}));

export default function AIIntroRAG() {
    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant="h3" fontWeight={900} color="#a78bfa">
                    UNDERSTANDING RAG
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.7 }}>
                    Beyond Simple Chatbots
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                {/* 1. The Core Concept */}
                <Grid item xs={12} md={7}>
                    <ContentCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <PsychologyIcon sx={{ color: "#a78bfa", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>What is RAG?</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, opacity: 0.9 }}>
                            **RAG (Retrieval-Augmented Generation)** is a technique that gives an LLM (like Gemini)
                            access to a specific, private dataset. Instead of guessing, the AI **retrieves** facts
                            from your documentation and **augments** the prompt with that information.
                        </Typography>



                        <Alert severity="info" sx={{ mt: 3, bgcolor: "rgba(167, 139, 250, 0.1)", color: "white", border: "1px solid #a78bfa" }}>
                            <strong>Architect's Rule:</strong> An LLM without RAG is like a student taking an exam
                            from memory. An LLM with RAG is like a student taking an exam with an open textbook.
                        </Alert>
                    </ContentCard>
                </Grid>

                {/* 2. The Math of Meaning */}
                <Grid item xs={12} md={5}>
                    <ContentCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <FunctionsIcon sx={{ color: "#FBBC04", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Semantic Math</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                            To find relevant documents, we convert text into a high-dimensional vector.
                            If two sentences are similar, the "distance" between their vectors is small.
                        </Typography>

                        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.3)", borderRadius: "8px", textAlign: "center", color: "#FBBC04" }}>
                            <Typography variant="caption" display="block" gutterBottom>Cosine Similarity Formula:</Typography>
                            <BlockMath math="\text{similarity} = \cos(\theta) = \frac{\mathbf{A} \cdot \mathbf{B}}{\|\mathbf{A}\| \|\mathbf{B}\|}" />
                        </Box>

                        <Typography variant="caption" sx={{ display: 'block', mt: 3, opacity: 0.6 }}>
                            Where <InlineMath math="\mathbf{A}" /> and <InlineMath math="\mathbf{B}" /> are vectors representing
                            your query and the document.
                        </Typography>
                    </ContentCard>
                </Grid>

                {/* 3. The Embedding Pipeline */}
                <Grid item xs={12}>
                    <ContentCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SearchIcon sx={{ color: "#34A853", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>The Retrieval Pipeline</Typography>
                        </Stack>



                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                    <Typography variant="subtitle2" color="#a78bfa">1. Chunking</Typography>
                                    <Typography variant="caption">Breaking long lessons into smaller pieces (e.g., 500 words each) so the AI can find specific facts.</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                    <Typography variant="subtitle2" color="#a78bfa">2. Embedding</Typography>
                                    <Typography variant="caption">Passing chunks through Gemini's <code>text-embedding-004</code> model to get the vector.</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", color: "white" }}>
                                    <Typography variant="subtitle2" color="#a78bfa">3. Vector Search</Typography>
                                    <Typography variant="caption">Storing these vectors in Firestore to allow for lightning-fast similarity searches.</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </ContentCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/ai-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#a78bfa", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
